import {
  BarController,
  BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, LineController, LineElement, PointElement, Tooltip,
} from 'chart.js';
import { useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import tinycolor from 'tinycolor2';
import type { DeepWritable } from 'ts-essentials';
import {
  ScrollBarComponent, ScrollbarType,
} from '~/_shared/components/scrollbar/scrollbar.component';
import { useTheme } from '~/_shared/themes/theme.hooks';
import { cloneDeep } from '~/_shared/utils/object/deepMerge';
import type { ChartItem } from './chartItem.types';

const WIDTH_PER_LABEL = 100; // Width of each label in pixels

const GRAPH_PRESET_COLORS = [
  '#E63946', '#9B5DE5', '#457B9D', '#6C757D', '#2A9D8F', '#FFB703',
];

const getAlternatingColor = (index: number, opacity = 0.75) => {
  const color = GRAPH_PRESET_COLORS[index % GRAPH_PRESET_COLORS.length];
  const c = tinycolor((color || GRAPH_PRESET_COLORS[0])).setAlpha(opacity);
  return c.toRgbString();
};

const normalizeData = (data: number[]) => {
  let min = Math.min(...data);
  if (min > 0) {
    min = 0;
  }
  const max = Math.max(...data);
  const range = max - min;

  // Avoid division by zero if all values are the same
  if (range === 0) {
    return data.map(() => 1);
  }

  return data.map(val => (((val - min) / range) * 100).toFixed(2));
};

export const ChartItemVisualizer = ({
  shouldNormalize,
  metrics,
}: ChartItem) => {

  const theme = useTheme();

  ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Legend, Tooltip, LineController, BarController);

  const mutableMetrics = useMemo(() => (
    // clone because the Chart.js library requires mutable objects
    cloneDeep(metrics) as DeepWritable<typeof metrics>
  ), [metrics]);

  const minAndMaxValuesPerMetric = mutableMetrics.barValues.map(metric => {
    return {
      min: Math.min(...metric.values),
      max: Math.max(...metric.values),
    };
  });

  const data = {
    labels: mutableMetrics.labels,
    datasets: [
      ...mutableMetrics.barValues.map((metric, index) => ({
        type: 'bar' as const,
        label: metric.name,
        rawData: metric.values,
        data: shouldNormalize
          ? normalizeData(metric.values)
          : metric.values,
        backgroundColor: getAlternatingColor(index),
        order: index + mutableMetrics.barValues.length,
      })),
      ...mutableMetrics.medians.map((metric, index) => {
        const value = shouldNormalize
          ? normalizeData([
            metric.value,
            minAndMaxValuesPerMetric[index]?.max || 0,
            minAndMaxValuesPerMetric[index]?.min || 0,
          ])[0] || 0
          : metric.value;
        const rawData = mutableMetrics.labels.map(() => metric.value);
        const data = mutableMetrics.labels.map(() => value);
        return {
          type: 'line' as const,
          label: metric.name,
          rawData,
          data,
          borderColor: getAlternatingColor(index, 1),
          backgroundColor: getAlternatingColor(index, 1),
          fill: false,
          tension: 0.4,
          pointRadius: 0, // Hide points in line chart
          order: index, // Ensure lines are drawn above bars
        };
      }),
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: theme.textColors.primary,
        },
        position: 'top' as const,
      },
      tooltip: {
        callbacks: {
          // Show the absolute raw value with thousand separators in the tooltip.
          label(context: {
            datasetIndex: number;
            dataIndex: number;
            raw: unknown;
          }) {
            const datasetIndex = context.datasetIndex;
            const index = context.dataIndex;
            const value = shouldNormalize
              ? (data.datasets[datasetIndex]?.rawData[index] || context.raw)
              : context.raw;
            const formattedValue = Number(value).toLocaleString();
            const label = data.datasets[datasetIndex]?.label || '';
            return label ? `${label}: ${formattedValue}` : formattedValue;
          },
        },
      },
    },
    scales: {
      y: {
        ticks: {
          color: theme.textColors.primary,
          ...(shouldNormalize ? {
            callback(value: number | string) {
              return `${value}%`;
            },
          } : {}),
        },
      },
      x: {
        ticks: {
          color: theme.textColors.primary,
        },
      },
    },
  };

  return (
    <div style={{ maxWidth: '100%', minWidth: 200 }}>
      <ScrollBarComponent
        style={{ maxWidth: '100%' }}
        type={ScrollbarType.Horizontal}
        translateContentHeightToHolder
        translateContentWidthToHolder
        contentStyle={{
          display: 'block',
          minWidth: 'none',
          width: data.labels.length * WIDTH_PER_LABEL,
          height: 238,
        }}
      >
        <Chart
          type="bar"
          data={data}
          options={options}
        />
      </ScrollBarComponent>
    </div>
  );
};
